import { CheckIcon } from "@heroicons/react/24/outline";
import Avatar from "components/core/Avatar";
import SadFaceIcon from "components/icons/SadFaceIcon";
import classNames from "components/ui/classNames";
import type { IAgendaQuery } from "lib/queries/__generated__/AgendaQuery.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import Link from "next/link";
import React from "react";
import {
  EarningsResultsEventDetails,
  PaymentOfDividendsEventDetails,
  PaymentOfStockDividendsEventDetails,
  RightsIssueEventDetails,
  SpinOffEventDetails,
  StockSplitEventDetails,
} from "./EventsCalendarDetails";
import { getEventNameWithEventType } from "./utils";

interface IDailyEventsCalendarProps {
  events?: IAgendaQuery;
  isLoading: boolean;
  isLoadingMore: boolean;

  onLoadMoreEvents: () => void;
  onGoToNextAgendaEvent: () => void;
}

export default function DailyEventsCalendar({
  events,
  isLoading,
  isLoadingMore,
  onLoadMoreEvents,
  onGoToNextAgendaEvent,
}: IDailyEventsCalendarProps) {
  return (
    <div className="md:mt-0">
      <ul className="space-y-2 text-sm leading-6">
        {isLoading && (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index}>
                <EventItemSkeleton />
              </li>
            ))}
          </>
        )}

        {events?.agenda.nodes.map(({ _id, eventDate, eventType, issuer, ...event }) => {
          if (
            issuer?.__typename !== "AmericanCompanyIssuer" &&
            issuer?.__typename !== "BrazilianCompanyIssuer" &&
            issuer?.__typename !== "BrazilianRealEstateFundIssuer"
          ) {
            return null;
          }

          const { name, tickerSymbolPrefix } = issuer;
          let linkHref;
          let linkAs;
          let issuerCountry: "US" | "BR" | undefined;

          if (issuer.__typename === "AmericanCompanyIssuer") {
            linkHref = "/us/[tickerSymbol]";
            linkAs = `/us/${tickerSymbolPrefix?.toLowerCase()}`;
            issuerCountry = "US";
          }

          if (issuer.__typename === "BrazilianCompanyIssuer" || issuer.__typename === "BrazilianRealEstateFundIssuer") {
            linkHref = "/br/[tickerSymbol]";
            linkAs = `/br/${tickerSymbolPrefix?.toLowerCase()}`;
            issuerCountry = "BR";
          }

          const parsedEventDate = parseDateYmd(eventDate);

          return (
            <li key={_id} className="relative">
              <Link href={linkHref || ""} as={linkAs} className="block hover:rounded-lg hover:bg-gray-100">
                <div className="flex items-center py-2 px-4">
                  <div className="flex min-w-0 flex-1 items-center">
                    <div className="group flex w-full min-w-0 items-center justify-between md:grid md:grid-cols-2 md:gap-4">
                      <div className="flex items-center space-x-4">
                        <Avatar
                          src={
                            issuer.__typename === "AmericanCompanyIssuer" ||
                            issuer.__typename === "BrazilianCompanyIssuer"
                              ? issuer.niceLogo?.url || null
                              : null
                          }
                          hashValue={issuer._id}
                          alt={issuer.name}
                          size={34}
                          squircle={true}
                        />

                        <div className="overflow-hidden">
                          <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-lg font-medium tracking-wider text-gray-600">
                            {name}
                          </p>
                          <div className="flex items-center gap-x-1">
                            <div className="text-md flex items-center justify-start tracking-wider text-gray-800 md:justify-center">
                              <time dateTime={parsedEventDate.toString()}>
                                {parsedEventDate.toLocaleDateString("pt-BR")}
                              </time>
                            </div>
                            {(event.__typename === "PaymentOfDividendsEvent" ||
                              event.__typename === "PaymentOfStockDividendsEvent") && (
                              <>
                                <div className="h-1 w-1 rounded-full bg-gray-800" />
                                <div className="text-gray-500">
                                  <span className="truncate">{event.symbol}</span>
                                </div>
                              </>
                            )}
                            <div className="h-1 w-1 rounded-full bg-gray-800" />
                            <div className="text-gray-500">
                              <span className="truncate">
                                {event.__typename === "EarningsResultsEvent"
                                  ? getEventNameWithEventType(eventType, event.eventFiscalYear)
                                  : getEventNameWithEventType(eventType)}
                              </span>
                            </div>
                            {event.__typename === "EarningsResultsEvent" && event.areFinancialsUpdated && (
                              <div className="ml-2 flex items-center text-green-600 md:hidden">
                                <CheckIcon className="h-5 w-5" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                </div>
              </Link>

              <div className="ml-16 sm:absolute sm:right-12 sm:top-5 sm:ml-0 md:right-72 lg:right-44 xl:right-60">
                <div className="hidden items-center md:flex">
                  <div className="text-xs font-medium uppercase">
                    {event.__typename === "EarningsResultsEvent" && (
                      <EarningsResultsEventDetails areFinancialsUpdated={event.areFinancialsUpdated} />
                    )}
                  </div>
                </div>

                {event.__typename === "PaymentOfDividendsEvent" && (
                  <PaymentOfDividendsEventDetails
                    amountPaid={event.amountPaid}
                    excludingDate={event.excludingDate}
                    eventDate={eventDate}
                    issuerCountry={issuerCountry}
                    recordDate={event.recordDate}
                  />
                )}

                {event.__typename === "PaymentOfStockDividendsEvent" && (
                  <PaymentOfStockDividendsEventDetails
                    excludingDate={event.excludingDate}
                    sharePaid={event.sharePaid}
                  />
                )}

                {event.__typename === "RightsIssueEvent" && (
                  <RightsIssueEventDetails
                    recordDate={event.recordDate}
                    eventDate={eventDate}
                    newShare={event.newShare}
                    subscriptionPrice={event.subscriptionPrice}
                    issuerCountry={issuerCountry}
                  />
                )}

                {event.__typename === "StockSplitEvent" && (
                  <StockSplitEventDetails
                    excludingDate={event.excludingDate}
                    splitFrom={event.splitFrom}
                    splitTo={event.splitTo}
                  />
                )}

                {event.__typename === "SpinOffEvent" && (
                  <SpinOffEventDetails
                    excludingDate={event.excludingDate}
                    spinFrom={event.spinFrom}
                    spinTo={event.spinTo}
                    numberOfShares={event.numberOfShares}
                    childCompanyName={event.childCompanyName}
                    childCompanyExchangeId={event.childCompanyExchangeId}
                    childCompanySymbol={event.childCompanySymbol}
                  />
                )}
              </div>
            </li>
          );
        })}

        {isLoadingMore && (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index}>
                <EventItemSkeleton />
              </li>
            ))}
          </>
        )}

        {!isLoading && !events?.agenda.nodes.length && (
          <li className="justify-center space-y-2 rounded-md p-8 text-center text-base tracking-wide text-gray-600">
            <div>
              <SadFaceIcon />
            </div>

            <p>
              Nenhum evento foi encontrado. Verifique se você pesquisou através do código completo, por ex:{" "}
              <strong>WEGE3</strong> ou <strong>AAPL</strong>.
            </p>

            {events?.agenda.nextUpcomingEventDateRange && (
              <div>
                <button
                  type="button"
                  className={classNames(
                    "mt-4 rounded-md border bg-white px-4 py-2 text-sm text-gray-500 shadow",
                    "hover:border-transparent hover:bg-brand hover:text-white",
                  )}
                  onClick={onGoToNextAgendaEvent}
                >
                  Ir para o próximo evento
                </button>
              </div>
            )}
          </li>
        )}
      </ul>
      {events?.agenda.pageInfo.hasNextPage && !isLoadingMore && (
        <div className="pt-8 text-center">
          <button
            disabled={isLoading}
            type="button"
            className={classNames(
              "font-aeonik text-base font-medium tracking-wide text-blue-600 hover:underline",
              "disabled:text-gray-600",
            )}
            onClick={onLoadMoreEvents}
          >
            Carregar mais eventos
          </button>
        </div>
      )}
    </div>
  );
}

function EventItemSkeleton() {
  return (
    <div className="py-2 px-4">
      <div className="flex animate-pulse items-start space-x-4">
        <div className="h-8 w-8 rounded-full bg-slate-200 squircle" />

        <div className="flex-1 space-y-2">
          <div className="h-3 w-32 rounded bg-slate-300" />
          <div className="h-2 w-2/3 rounded bg-slate-200" />
          <div className="h-2 w-36 rounded bg-slate-200" />
        </div>
      </div>
    </div>
  );
}

function ChevronRightIcon(props: React.SVGProps<any>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" {...props}>
      <path
        fillRule="evenodd"
        d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}
